/**
 * TeamVacationPage.js
 *
 * Main page for Team Vacation Management - displays an overview of all vacation days
 * for staff members across the company or department.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useVacation } from '../../contexts/VacationContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useStaff } from '../../contexts/StaffContext';
import { HolidayProvider, useHolidayContext } from '../../contexts/HolidayContext';
import TeamVacationCalendar from '../../components/vacation/TeamVacationCalendar';
import VacationModal from '../../components/schedule/VacationModal';
import '../../styles/vacation.css';

/**
 * Inner component that uses HolidayContext
 * This must be wrapped by HolidayProvider
 */
const TeamVacationPageContent = () => {
  const { t, i18n } = useTranslation('vacation');
  const { currentUser, userRole } = useAuth();
  const { departments, loading: departmentsLoading } = useDepartment();
  const { activeStaff: staffMembers, loading: staffLoading } = useStaff();
  const { sortedHolidays } = useHolidayContext();
  const {
    vacations,
    loading: vacationsLoading,
    subscribeToVacations,
    unsubscribe,
    fetchVacationsByStaffAndMonth,
  } = useVacation();

  // Filter state
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [selectedStaffId, setSelectedStaffId] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);

  /**
   * Get accessible departments based on user role
   * Owner: All departments
   * Manager: Only assigned departments
   */
  const accessibleDepartments = useMemo(() => {
    if (userRole === 'owner') {
      return departments;
    } else if (userRole === 'manager') {
      return departments.filter(dept =>
        currentUser?.departmentIds?.includes(dept.id)
      );
    }
    return [];
  }, [departments, userRole, currentUser]);

  /**
   * Filter staff based on selected department
   */
  const filteredStaff = useMemo(() => {
    if (selectedDepartmentId === 'all') {
      const accessibleDeptIds = accessibleDepartments.map(d => d.id);
      return staffMembers.filter(s =>
        s.departmentIds?.some(deptId => accessibleDeptIds.includes(deptId))
      );
    }
    return staffMembers.filter(s =>
      s.departmentIds?.includes(selectedDepartmentId)
    );
  }, [selectedDepartmentId, staffMembers, accessibleDepartments]);

  /**
   * Calculate month date range
   */
  const getMonthDateRange = useCallback((monthDate) => {
    const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    startOfMonth.setHours(0, 0, 0, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    return { startOfMonth, endOfMonth };
  }, []);

  /**
   * Fetch/Subscribe to vacations when filters change
   */
  useEffect(() => {
    if (departmentsLoading || staffLoading) return;

    const { startOfMonth, endOfMonth } = getMonthDateRange(currentMonth);

    if (selectedStaffId !== 'all') {
      fetchVacationsByStaffAndMonth(selectedStaffId, startOfMonth, endOfMonth);
      return;
    }

    if (selectedDepartmentId !== 'all') {
      const unsubscribeFn = subscribeToVacations(
        startOfMonth,
        endOfMonth,
        [selectedDepartmentId]
      );
      return () => { if (unsubscribeFn) unsubscribeFn(); };
    }

    if (userRole === 'owner') {
      const unsubscribeFn = subscribeToVacations(startOfMonth, endOfMonth, null);
      return () => { if (unsubscribeFn) unsubscribeFn(); };
    }

    if (userRole === 'manager' && accessibleDepartments.length > 0) {
      const deptIds = accessibleDepartments.map(d => d.id);
      const unsubscribeFn = subscribeToVacations(startOfMonth, endOfMonth, deptIds);
      return () => { if (unsubscribeFn) unsubscribeFn(); };
    }

    return () => { unsubscribe(); };
  }, [
    selectedDepartmentId,
    selectedStaffId,
    currentMonth,
    userRole,
    accessibleDepartments,
    departmentsLoading,
    staffLoading,
    subscribeToVacations,
    unsubscribe,
    fetchVacationsByStaffAndMonth,
    getMonthDateRange,
  ]);

  /**
   * Cleanup subscriptions on unmount
   */
  useEffect(() => {
    return () => { unsubscribe(); };
  }, [unsubscribe]);

  /**
   * Handle month navigation
   */
  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  /**
   * Handle department filter change
   */
  const handleDepartmentChange = useCallback((e) => {
    setSelectedDepartmentId(e.target.value);
    setSelectedStaffId('all');
  }, []);

  /**
   * Handle staff filter change
   */
  const handleStaffChange = useCallback((e) => {
    setSelectedStaffId(e.target.value);
  }, []);

  /**
   * Handle add vacation modal
   */
  const handleOpenAddModal = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  /**
   * Format month display using active i18n language
   */
  const monthDisplay = useMemo(() => {
    return currentMonth.toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });
  }, [currentMonth, i18n.language]);

  const isLoading = departmentsLoading || staffLoading || vacationsLoading;

  return (
    <div className="team-vacation-page">
      {/* Header */}
      <div className="team-vacation-header">
        <div className="header-content">
          <h1>{t('title')}</h1>
          <p className="subtitle">{t('subtitle')}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleOpenAddModal}
          disabled={isLoading}
        >
          + {t('addVacation')}
        </button>
      </div>

      {/* Filters and Month Navigation */}
      <div className="vacation-filters">
        {/* Department Filter */}
        <div className="filter-group">
          <label htmlFor="department-filter">{t('labels.department')}</label>
          <select
            id="department-filter"
            value={selectedDepartmentId}
            onChange={handleDepartmentChange}
            disabled={isLoading || accessibleDepartments.length === 0}
          >
            <option value="all">{t('allDepartments')}</option>
            {accessibleDepartments.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
          {accessibleDepartments.length === 0 && !departmentsLoading && (
            <small className="text-muted">
              {t('messages.noDepartmentsAvailable')}
            </small>
          )}
        </div>

        {/* Staff Filter */}
        <div className="filter-group">
          <label htmlFor="staff-filter">{t('labels.staff')}</label>
          <select
            id="staff-filter"
            value={selectedStaffId}
            onChange={handleStaffChange}
            disabled={isLoading || filteredStaff.length === 0}
          >
            <option value="all">{t('allStaff')}</option>
            {filteredStaff.map(member => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
          {filteredStaff.length === 0 && !staffLoading && (
            <small className="text-muted">
              {t('messages.noStaffAvailable')}
            </small>
          )}
        </div>

        {/* Month Navigation */}
        <div className="filter-group month-navigation-inline">
          <label>&nbsp;</label>
          <div className="month-navigation-controls">
            <button
              className="btn btn-icon"
              onClick={handlePreviousMonth}
              disabled={isLoading}
              aria-label={t('calendar.previousMonth')}
              title={t('calendar.previousMonth')}
            >
              ←
            </button>
            <span className="month-display">{monthDisplay}</span>
            <button
              className="btn btn-icon"
              onClick={handleNextMonth}
              disabled={isLoading}
              aria-label={t('calendar.nextMonth')}
              title={t('calendar.nextMonth')}
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('messages.loadingVacations')}</p>
        </div>
      )}

      {/* No Access Message */}
      {!isLoading && accessibleDepartments.length === 0 && (
        <div className="no-access-message">
          <p>{t('messages.noAccessDepartments')}</p>
          <p>{t('messages.contactAdministrator')}</p>
        </div>
      )}

      {/* Calendar */}
      {!isLoading && accessibleDepartments.length > 0 && (
        <TeamVacationCalendar
          currentMonth={currentMonth}
          vacations={vacations}
          staff={filteredStaff}
          departments={accessibleDepartments}
          selectedDepartmentId={selectedDepartmentId}
          selectedStaffId={selectedStaffId}
          holidays={sortedHolidays}
        />
      )}

      {/* Add Vacation Modal */}
      {showAddModal && (
        <VacationModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          departments={accessibleDepartments}
          staff={filteredStaff}
          mode="add"
        />
      )}
    </div>
  );
};

/**
 * Outer component that wraps with HolidayProvider
 */
const TeamVacationPage = () => {
  const { t } = useTranslation('vacation');
  const { currentCompany } = useAuth();

  if (!currentCompany?.id) {
    return (
      <div className="team-vacation-page">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('messages.loadingCompany')}</p>
        </div>
      </div>
    );
  }

  return (
    <HolidayProvider companyId={currentCompany.id}>
      <TeamVacationPageContent />
    </HolidayProvider>
  );
};

export default TeamVacationPage;